/**
 * @file THE MISS MODEL — the one place that decides whether a shot connects and,
 * when it does not, WHERE it lands instead.
 *
 * Shots in this project are hitscan: the manager (or the hero's auto-attack)
 * applies the damage on the same step the trigger is pulled, and the tracer is
 * feedback played after the fact (`render/Tracers.js`). Accuracy therefore is
 * NOT a physics problem — there is no bullet to deflect — it is one roll plus a
 * displaced impact POINT for the tracer to fly at. That keeps the whole thing
 * pure, node-testable, and impossible to desync from the damage.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE CURVE
 * ─────────────────────────────────────────────────────────────────────────────
 * Miss chance is zero out to `pointBlank`, then rises as `t^falloff` to
 * `maxMiss` at the weapon's own `range` (never past it — a shot beyond range is
 * not taken at all). `falloff > 1` is load-bearing: a LINEAR ramp already misses
 * ~20 % at half range, which reads as a broken gun in a horde game where most
 * shooting happens mid-band. The exponent keeps close-quarters reliable and
 * spends the whole error budget at the edge, which is where a miss reads as
 * "that was a long shot" rather than "the game cheated".
 *
 * A miss is displaced MOSTLY SIDEWAYS (perpendicular to the shot line) and only
 * a little along it, for two reasons: a lateral miss is legible at the game's
 * 52-degree pitch, where an along-the-line miss is hidden behind the target; and
 * `minSpread` guarantees the tracer visibly clears the enemy, because a "miss"
 * whose tracer still ends inside the sprite is a bug report waiting to happen.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * RNG (rule 4)
 * ─────────────────────────────────────────────────────────────────────────────
 * Every draw comes from a stream the CALLER owns, and the callers all use a
 * dedicated `'aim'` stream rather than the manager's `'enemies'` one. That is
 * not tidiness: `tests/hero.test.mjs` pins a golden checksum of a 400-step hero
 * fight, and it would move the moment an aim roll shared the population stream.
 * Same reason `EnemyManager.accuracy` defaults to null — hero mode draws nothing
 * here unless it is asked to.
 *
 * The draw COUNT is deliberately variable (1 on a hit, 4 on a miss) and that is
 * fine: it is one stream, consumed in one order, so a seed still replays.
 *
 * @module engine/systems/accuracy
 */

/** @typedef {import('../types.js').Rng} Rng */

/**
 * @typedef {Object} AccuracySpec
 * @property {number} pointBlank Metres inside which nothing ever misses.
 * @property {number} maxMiss Miss probability at the weapon's max range.
 * @property {number} falloff Curve exponent; > 1 favours close shots.
 * @property {number} spread Extra lateral scatter at max range, metres.
 * @property {number} minSpread Metres a miss ALWAYS clears the target by.
 * @property {number} longSpread Along-the-line error, as a fraction of lateral.
 */

/**
 * Shipped defaults, tuned against the city demo's 9 m auto-attack: dead-on
 * inside 3 m, ~40 % misses at the range edge, and a miss that lands roughly one
 * body-width to the side.
 * @type {AccuracySpec}
 */
export const ACCURACY = Object.freeze({
  pointBlank: 3,
  maxMiss: 0.4,
  falloff: 1.6,
  spread: 0.85,
  minSpread: 0.45,
  longSpread: 0.35,
});

/**
 * @typedef {Object} Shot The result of {@link rollShot}. Reused in place by the
 *   callers — never hold a reference across steps.
 * @property {boolean} hit
 * @property {number} x Impact X: the target when `hit`, the scatter point when not.
 * @property {number} z Impact Z.
 * @property {number} dist Metres shooter -> target (NOT to the impact point).
 */

/** @returns {Shot} A zeroed scratch record to pass into {@link rollShot}. */
export const newShot = () => ({ hit: true, x: 0, z: 0, dist: 0 });

/**
 * @param {number} dist Metres to the target.
 * @param {number} range The weapon's max range, metres.
 * @param {AccuracySpec} [a]
 * @returns {number} 0..`a.maxMiss`.
 */
export function missChance(dist, range, a = ACCURACY) {
  if (!(range > a.pointBlank)) return 0;
  const t = (dist - a.pointBlank) / (range - a.pointBlank);
  if (!(t > 0)) return 0;
  return a.maxMiss * Math.pow(t > 1 ? 1 : t, a.falloff);
}

/**
 * Rolls one shot and writes the impact point into `out`.
 *
 * Draws from `rng`: ONE number on a hit, FOUR on a miss (roll, side, lateral,
 * along). See the module header on why that is safe.
 *
 * @param {Rng} rng A dedicated aim stream — see the header.
 * @param {Shot} out Scratch from {@link newShot}, written in place.
 * @param {number} sx Shooter X. @param {number} sz Shooter Z.
 * @param {number} tx Target X. @param {number} tz Target Z.
 * @param {number} range The weapon's max range, metres.
 * @param {AccuracySpec} [a]
 * @returns {Shot} `out`.
 */
export function rollShot(rng, out, sx, sz, tx, tz, range, a = ACCURACY) {
  const dx = tx - sx;
  const dz = tz - sz;
  const dist = Math.hypot(dx, dz);
  out.hit = true;
  out.x = tx;
  out.z = tz;
  out.dist = dist;
  if (dist < 1e-4) return out;

  const p = missChance(dist, range, a);
  // The `p <= 0` short-circuit matters: inside `pointBlank` no number is drawn
  // at all, so a point-blank brawl cannot advance the stream frame by frame.
  if (p <= 0 || rng() >= p) return out;

  out.hit = false;
  const t = Math.min(1, (dist - a.pointBlank) / Math.max(1e-3, range - a.pointBlank));
  const s = a.minSpread + a.spread * t;
  const ux = dx / dist;
  const uz = dz / dist;
  const side = rng() < 0.5 ? -1 : 1;
  // 0.55..1 of the budget — never near zero, or the shot "misses" through the
  // enemy's chest.
  const lateral = side * s * (0.55 + 0.45 * rng());
  const along = (rng() * 2 - 1) * s * a.longSpread;
  // Perpendicular of (ux,uz) in XZ is (-uz,ux).
  out.x = tx - uz * lateral + ux * along;
  out.z = tz + ux * lateral + uz * along;
  return out;
}
